/**
 * * Places role bubbles and document cards inside a circle.
 * * Repulsion keeps a gap. Links pull their ends closer. A dragged bubble
 * * stays put and pushes the others, which push their neighbours in turn.
 */

export interface LayoutItem {
  id: string
  kind: 'role' | 'document'
  /** * Liaison roles stay on the circle line. */
  ring?: boolean
  /** * Folded circle: one bubble, internal ports on the left and external ports on the right. */
  collapsed?: boolean
  x: number
  y: number
  width: number
  height: number
}

export const COLLAPSED_RADIUS = 74

export type RolePort = 'in' | 'out' | 'external-in' | 'external-out'

export type LiaisonSide = 'left' | 'right' | 'top' | 'bottom'

export const CIRCLE_STRIDE = 760

export interface LayoutLink {
  sourceId: string
  targetId: string
}

export interface CirclePlacement {
  radius: number
  items: LayoutItem[]
}

export interface PortHit {
  circleId: string
  roleId: string
  port: RolePort
}

const GAP = 14

export function circleRadius(itemCount: number): number {
  return Math.max(176, 108 + itemCount * 26)
}

export function roleSize(name: string): { width: number; height: number } {
  const width = Math.min(148, Math.max(96, 28 + name.trim().length * 7))
  return { width, height: 46 }
}

export const DOCUMENT_SIZE = { width: 78, height: 54 }

/**
 * * Stable generator so the first automatic placement does not jump on reload.
 */
export function seededRandom(seed: number): () => number {
  let state = seed >>> 0
  return () => {
    state = (Math.imul(state, 1664525) + 1013904223) >>> 0
    return state / 4294967296
  }
}

export function hashSeed(text: string): number {
  let hash = 2166136261
  for (const char of text) {
    hash = Math.imul(hash ^ char.charCodeAt(0), 16777619)
  }
  return hash >>> 0
}

/**
 * * Separates overlapping items. The pinned item does not move.
 * * releasePinned lets that item leave the disk (a role dragged toward another circle).
 * * A ring item stays on the circumference and is not pushed inward.
 */
export function pushLayout(
  items: LayoutItem[],
  pinnedId: string | null,
  radius: number,
  releasePinned = false,
): LayoutItem[] {
  const next = items.map((item) => ({ ...item }))
  for (let step = 0; step < 14; step += 1) {
    for (let i = 0; i < next.length; i += 1) {
      for (let j = i + 1; j < next.length; j += 1) {
        const left = next[i]
        const right = next[j]
        if (!left || !right) {
          continue
        }
        const push = separation(left, right)
        if (!push) {
          continue
        }
        if (left.ring && right.ring) {
          continue
        }
        if (left.ring) {
          if (right.id !== pinnedId) {
            right.x += push.x
            right.y += push.y
          }
          continue
        }
        if (right.ring) {
          if (left.id !== pinnedId) {
            left.x -= push.x
            left.y -= push.y
          }
          continue
        }
        const leftPinned = left.id === pinnedId
        const rightPinned = right.id === pinnedId
        if (leftPinned && rightPinned) {
          continue
        }
        if (leftPinned) {
          right.x += push.x
          right.y += push.y
        } else if (rightPinned) {
          left.x -= push.x
          left.y -= push.y
        } else {
          left.x -= push.x / 2
          left.y -= push.y / 2
          right.x += push.x / 2
          right.y += push.y / 2
        }
      }
    }
    for (const item of next) {
      settle(item, radius, item.id === pinnedId && releasePinned)
    }
  }
  return next
}

/**
 * * Scatters items, then shortens links without letting bubbles overlap.
 */
export function distributeLayout(
  items: LayoutItem[],
  links: LayoutLink[],
  radius: number,
  random: () => number,
): LayoutItem[] {
  const next = items.map((item) => {
    const angle = random() * Math.PI * 2
    if (item.ring) {
      return {
        ...item,
        x: Math.cos(angle) * radius,
        y: Math.sin(angle) * radius,
      }
    }
    const ring = (0.22 + random() * 0.5) * Math.max(24, radius - 80)
    return {
      ...item,
      x: Math.cos(angle) * ring,
      y: Math.sin(angle) * ring,
    }
  })
  for (let step = 0; step < 40; step += 1) {
    for (let i = 0; i < next.length; i += 1) {
      for (let j = i + 1; j < next.length; j += 1) {
        const left = next[i]
        const right = next[j]
        if (!left || !right || left.ring || right.ring) {
          continue
        }
        const push = separation(left, right)
        if (!push) {
          continue
        }
        left.x -= push.x / 2
        left.y -= push.y / 2
        right.x += push.x / 2
        right.y += push.y / 2
      }
    }
    for (const link of links) {
      const source = next.find((item) => item.id === link.sourceId)
      const target = next.find((item) => item.id === link.targetId)
      if (!source || !target || source.ring || target.ring) {
        continue
      }
      const dx = target.x - source.x
      const dy = target.y - source.y
      const distance = Math.hypot(dx, dy) || 1
      const preferred = 110
      if (distance <= preferred) {
        continue
      }
      const pull = (distance - preferred) * 0.06
      source.x += (dx / distance) * pull
      source.y += (dy / distance) * pull
      target.x -= (dx / distance) * pull
      target.y -= (dy / distance) * pull
    }
    for (const item of next) {
      settle(item, radius, false)
    }
  }
  return pushLayout(next, null, radius)
}

/**
 * * Puts a liaison on the circle line. A point at the centre goes to the right.
 */
export function projectOnCircle(x: number, y: number, radius: number): { x: number; y: number } {
  const distance = Math.hypot(x, y)
  if (distance < 1) {
    return { x: radius, y: 0 }
  }
  return { x: (x / distance) * radius, y: (y / distance) * radius }
}

/**
 * * The side of the circle the liaison occupies. That side faces outward.
 */
export function liaisonSide(x: number, y: number): LiaisonSide {
  if (Math.abs(x) >= Math.abs(y)) {
    return x >= 0 ? 'right' : 'left'
  }
  return y >= 0 ? 'bottom' : 'top'
}

export function isOutput(port: RolePort): boolean {
  return port === 'out' || port === 'external-out'
}

export function isInput(port: RolePort): boolean {
  return port === 'in' || port === 'external-in'
}

export function isExternal(port: RolePort): boolean {
  return port === 'external-in' || port === 'external-out'
}

/**
 * * Port centre in the same coordinates as the bubble centre.
 * * Internal ports face the disk. External ports face away from it.
 * * On the left or the right they stack vertically. Above or below they sit side by side.
 */
export function portAnchor(item: Pick<LayoutItem, 'x' | 'y' | 'width' | 'height' | 'ring' | 'collapsed'>, port: RolePort): { x: number; y: number } {
  if (item.collapsed) {
    const along = 9
    const reach = 8
    const external = isExternal(port)
    const output = isOutput(port)
    const edge = (external ? 1 : -1) * (item.width / 2 + reach)
    return { x: item.x + edge, y: item.y + (output ? along : -along) }
  }
  if (!item.ring) {
    const outward = port === 'out' || port === 'external-out' ? 1 : -1
    return { x: item.x + outward * (item.width / 2), y: item.y }
  }
  const side = liaisonSide(item.x, item.y)
  const along = 9
  const reach = 14
  const external = isExternal(port)
  const output = isOutput(port)
  if (side === 'left' || side === 'right') {
    const outward = side === 'right' ? 1 : -1
    const edge = (external ? outward : -outward) * (item.width / 2 + reach)
    return { x: item.x + edge, y: item.y + (output ? along : -along) }
  }
  const outward = side === 'bottom' ? 1 : -1
  const edge = (external ? outward : -outward) * (item.height / 2 + reach)
  return { x: item.x + (output ? along : -along), y: item.y + edge }
}

export interface CircleFrame {
  id: string
  x: number
  y: number
  radius: number
}

/**
 * * Keeps a gap between circles. The dragged circle stays under the pointer.
 * * The others move, then push their neighbours, the same way role bubbles do.
 */
export function separateCircles(frames: CircleFrame[], pinnedId: string): CircleFrame[] {
  const next = frames.map((frame) => ({ ...frame }))
  for (let step = 0; step < 16; step += 1) {
    for (let i = 0; i < next.length; i += 1) {
      for (let j = i + 1; j < next.length; j += 1) {
        const left = next[i]
        const right = next[j]
        if (!left || !right) {
          continue
        }
        const ax = left.x + left.radius
        const ay = left.y + left.radius
        const bx = right.x + right.radius
        const by = right.y + right.radius
        let dx = bx - ax
        let dy = by - ay
        let distance = Math.hypot(dx, dy)
        const minimum = left.radius + right.radius + GAP
        if (distance >= minimum) {
          continue
        }
        if (distance < 0.01) {
          dx = 1
          dy = 0
          distance = 1
        }
        const push = minimum - distance
        const ux = dx / distance
        const uy = dy / distance
        const leftPinned = left.id === pinnedId
        const rightPinned = right.id === pinnedId
        if (leftPinned) {
          right.x += ux * push
          right.y += uy * push
        } else if (rightPinned) {
          left.x -= ux * push
          left.y -= uy * push
        } else {
          left.x -= (ux * push) / 2
          left.y -= (uy * push) / 2
          right.x += (ux * push) / 2
          right.y += (uy * push) / 2
        }
      }
    }
  }
  return next
}

/**
 * * Separates circles that share the same corner so several are visible at once.
 * * Returns null when they already sit apart.
 */
export function spreadCircleOrigins<T extends { id: string; x: number; y: number }>(
  circles: T[],
): Array<{ id: string; x: number; y: number }> | null {
  let crowded = false
  for (let i = 0; i < circles.length; i += 1) {
    for (let j = i + 1; j < circles.length; j += 1) {
      const left = circles[i]
      const right = circles[j]
      if (!left || !right) {
        continue
      }
      if (Math.hypot(left.x - right.x, left.y - right.y) < 80) {
        crowded = true
      }
    }
  }
  if (!crowded) {
    return null
  }
  return circles.map((circle, index) => ({
    id: circle.id,
    x: 48 + (index % 3) * CIRCLE_STRIDE,
    y: 48 + Math.floor(index / 3) * CIRCLE_STRIDE,
  }))
}

/**
 * * True when the segment crosses another role bubble.
 * * The two ends of the link are ignored.
 */
export function linkCrossesRole(
  x1: number,
  y1: number,
  x2: number,
  y2: number,
  items: LayoutItem[],
  sourceId: string,
  targetId: string,
): boolean {
  return items.some((item) => (
    item.kind === 'role'
    && item.id !== sourceId
    && item.id !== targetId
    && segmentHitsBox(x1, y1, x2, y2, item)
  ))
}

export function segmentHitsBox(
  x1: number,
  y1: number,
  x2: number,
  y2: number,
  box: Pick<LayoutItem, 'x' | 'y' | 'width' | 'height'>,
): boolean {
  const minX = box.x - box.width / 2
  const maxX = box.x + box.width / 2
  const minY = box.y - box.height / 2
  const maxY = box.y + box.height / 2
  if (pointInRect(x1, y1, minX, minY, maxX, maxY) || pointInRect(x2, y2, minX, minY, maxX, maxY)) {
    return true
  }
  return (
    segmentsIntersect(x1, y1, x2, y2, minX, minY, maxX, minY)
    || segmentsIntersect(x1, y1, x2, y2, maxX, minY, maxX, maxY)
    || segmentsIntersect(x1, y1, x2, y2, maxX, maxY, minX, maxY)
    || segmentsIntersect(x1, y1, x2, y2, minX, maxY, minX, minY)
  )
}

function separation(left: LayoutItem, right: LayoutItem): { x: number; y: number } | null {
  const dx = right.x - left.x
  const dy = right.y - left.y
  const overlapX = (left.width + right.width) / 2 + GAP - Math.abs(dx)
  const overlapY = (left.height + right.height) / 2 + GAP - Math.abs(dy)
  if (overlapX <= 0 || overlapY <= 0) {
    return null
  }
  if (overlapX < overlapY) {
    const sign = dx === 0 ? 1 : Math.sign(dx)
    return { x: sign * overlapX, y: 0 }
  }
  const sign = dy === 0 ? 1 : Math.sign(dy)
  return { x: 0, y: sign * overlapY }
}

function settle(item: LayoutItem, radius: number, release: boolean): void {
  if (item.ring) {
    const projected = projectOnCircle(item.x, item.y, radius)
    item.x = projected.x
    item.y = projected.y
    return
  }
  if (release) {
    return
  }
  clampInside(item, radius)
}

function clampInside(item: LayoutItem, radius: number): void {
  const margin = Math.hypot(item.width, item.height) / 2 + 10
  const limit = Math.max(8, radius - margin)
  const distance = Math.hypot(item.x, item.y)
  if (distance > limit) {
    item.x = (item.x / distance) * limit
    item.y = (item.y / distance) * limit
  }
}

function pointInRect(x: number, y: number, minX: number, minY: number, maxX: number, maxY: number): boolean {
  return x >= minX && x <= maxX && y >= minY && y <= maxY
}

function segmentsIntersect(
  ax: number,
  ay: number,
  bx: number,
  by: number,
  cx: number,
  cy: number,
  dx: number,
  dy: number,
): boolean {
  const d1 = direction(cx, cy, dx, dy, ax, ay)
  const d2 = direction(cx, cy, dx, dy, bx, by)
  const d3 = direction(ax, ay, bx, by, cx, cy)
  const d4 = direction(ax, ay, bx, by, dx, dy)
  return ((d1 > 0 && d2 < 0) || (d1 < 0 && d2 > 0)) && ((d3 > 0 && d4 < 0) || (d3 < 0 && d4 > 0))
}

function direction(ax: number, ay: number, bx: number, by: number, cx: number, cy: number): number {
  return (cx - ax) * (by - ay) - (cy - ay) * (bx - ax)
}
